import { useState, useEffect } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, Eye, Users, LayoutList, Search, Filter, BarChart3, TrendingUp, MessageCircle, Star, AlertTriangle, ArrowUp, Store, Lock, RefreshCw, ExternalLink, LayoutDashboard, UserPlus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserAvatar from "@/components/UserAvatar";
import AffiliatesTab from "@/components/AffiliatesTab";
import AdminOverview from "@/components/admin/AdminOverview";
import { BreakdownGrid } from "@/components/ui/stat-modal";
import * as XLSX from "xlsx";

interface KPIs {
  total_users: number;
  new_users_7d: number;
  new_users_30d: number;
  blocked_users: number;
  total_listings: number;
  active_listings: number;
  pending_listings: number;
  paused_listings: number;
  rejected_listings: number;
  new_listings_7d: number;
  new_listings_30d: number;
  total_conversations: number;
  new_conversations_7d: number;
  total_messages: number;
  new_messages_7d: number;
  total_reviews: number;
  reported_reviews: number;
  avg_rating: number;
}

interface PendingListing {
  id: string;
  title: string;
  category: string;
  description: string;
  price: number;
  price_unit: string;
  city: string;
  state: string;
  images: string[];
  owner_name: string;
  owner_id: string;
  owner_email: string;
  created_at: string;
  status: string;
  listing_type: string;
  rejection_reason?: string;
  ticket_number?: number;
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  account_type: string;
  user_code: string;
  created_at: string;
  is_admin: boolean;
  blocked: boolean;
  block_reason: string;
}


function formatPhone(value: string): string {
  const digits = (value || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
export default function AdminPage() {
  const { user, authLoading, isPromoted } = useApp();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as "overview" | "listings" | "users" | "affiliates" | "stores") || "overview";
  const setActiveTab = (tab: "overview" | "listings" | "users" | "affiliates" | "stores") => {
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("tab", tab); return p; });
  };

  // Growth data state
  const [growthData, setGrowthData] = useState<any[]>([]);
  const [growthPeriod, setGrowthPeriod] = useState(30);

  // Growth data state

  // KPIs state
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [kpisLoading, setKpisLoading] = useState(true);

  // Listings state
  const [listings, setListings] = useState<PendingListing[]>([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [selected, setSelected] = useState<PendingListing | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ id: string; title: string } | null>(null);
  const [editRejectModal, setEditRejectModal] = useState<{ id: string; listingId: string; message: string } | null>(null);
  const [editRejectMsg, setEditRejectMsg] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [stores, setStores] = useState<any[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  async function loadStores() {
    setStoresLoading(true);
    const { data, error } = await supabase.rpc("admin_list_stores");
    if (error) { console.error("[admin_list_stores]", error); alert("Erro ao listar lojas: " + error.message); }
    setStores(data || []);
    setStoresLoading(false);
  }
  useEffect(() => { if (activeTab === "stores") loadStores(); // eslint-disable-next-line
  }, [activeTab]);
  async function adminResetSlug(ownerId: string) {
    if (!confirm("Liberar o nome da loja? O dono poderá escolher um novo nome.")) return;
    await supabase.rpc("admin_reset_store_slug", { p_owner: ownerId });
    loadStores();
  }
  async function adminToggleStore(ownerId: string, enabled: boolean) {
    await supabase.rpc("admin_set_store_enabled", { p_owner: ownerId, p_enabled: !enabled });
    loadStores();
  }
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectMsg, setBulkRejectMsg] = useState("");
  const statusFilter = (searchParams.get("status") as "pending" | "active" | "paused" | "rejected" | "expired") || "pending";
  const setStatusFilter = (s: "pending" | "active" | "paused" | "rejected" | "expired") => {
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("status", s); return p; });
  };
  const [userFilter, setUserFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [planSaved, setPlanSaved] = useState<string | null>(null);
  const [planBlockModal, setPlanBlockModal] = useState<{ userId: string; planId: string } | null>(null);
  const [planJustification, setPlanJustification] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [listingSort, setListingSort] = useState<"newest" | "oldest" | "price_asc" | "price_desc" | "city">("newest");
  const [userSort, setUserSort] = useState<"newest" | "oldest" | "alpha" | "listings">("newest");
  const [typeFilter, setTypeFilter] = useState("all");

  // Users state
  const [exportDays, setExportDays] = useState("90");
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => { checkAdmin(); }, [user]);
  useEffect(() => { if (isAdmin) { fetchListings(); fetchUsers(); fetchPendingCount(); fetchKpis(); } }, [isAdmin, statusFilter]);
  useEffect(() => { if (isAdmin) fetchGrowthData(); }, [isAdmin, growthPeriod]);
  // Atualiza KPIs quando o modal de rejeicao fecha (ex.: rejeitou pelo pop-up da Visao Geral)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin && !rejectModal) { fetchKpis(); fetchPendingCount(); } }, [rejectModal]);

  async function fetchGrowthData() {
    const { data } = await supabase.rpc("get_growth_data", { days_back: growthPeriod });
    if (!data) return;
    console.log("growthData raw:", JSON.stringify(data[0]));

    if (growthPeriod <= 90) {
      // Diario — mostrar todos os pontos
      const mapped = data.map((d: any) => ({
        day: new Date(d.d + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        new_users: Number(d.users_count),
        new_listings: Number(d.listings_count),
        new_messages: Number(d.messages_count),
        new_conversations: Number(d.conversations_count),
      }));
      let accUsers = 0;
      const withTotal = mapped.map((d: any) => {
        accUsers += d.new_users;
        return { ...d, total_users: accUsers };
      });
      setGrowthData(withTotal);
    } else {
      // Agrupar por mes para periodos longos
      const monthly: Record<string, any> = {};
      data.forEach((d: any) => {
        const date = new Date(d.d + "T12:00:00");
        const key = date.toLocaleDateString("pt-BR", { month: "2-digit", year: "2-digit" });
        if (!monthly[key]) {
          monthly[key] = {
            day: key,
            new_users: 0,
            new_listings: 0,
            new_messages: 0,
            new_conversations: 0,
          };
        }
        monthly[key].new_users += Number(d.users_count);
        monthly[key].new_listings += Number(d.listings_count);
        monthly[key].new_messages += Number(d.messages_count);
        monthly[key].new_conversations += Number(d.conversations_count);
      });
      setGrowthData(Object.values(monthly));
    }
  }

  async function fetchKpis() {
    setKpisLoading(true);
    const { data } = await supabase.from("admin_kpis").select("*").single();
    if (data) setKpis(data);
    setKpisLoading(false);
  }

  async function fetchPendingCount() {
    const { count } = await supabase
      .from("listings")
      .select("*", { count: "exact", head: true })
      .eq("status", "pending");
    setPendingCount(count || 0);
  }

  async function checkAdmin() {
    if (!user) { setIsAdmin(false); return; }
    const { data } = await supabase.rpc("get_my_profile").select("is_admin").single();
    setIsAdmin(data?.is_admin || false);
  }

  async function fetchListings() {
    setListingsLoading(true);
    const { data } = await supabase
      .from("listings")
      .select("*, owner:profiles!listings_owner_id_fkey(user_code)")
      .eq("status", statusFilter)
      .order("bumped_at", { ascending: false });
    if (data) setListings(data);
    setListingsLoading(false);
  }

  async function fetchUsers() {
    setUsersLoading(true);
    const { data } = await supabase
      .rpc("admin_list_users")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setUsers(data);
    setUsersLoading(false);
  }

  async function toggleBlock(userId: string, current: boolean) {
    const reason = current ? null : prompt("Motivo do bloqueio (opcional):");
    if (!current && reason === null) return; // cancelou
    await supabase.from("profiles").update({
      blocked: !current,
      block_reason: reason || null,
    }).eq("id", userId);
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, blocked: !current, block_reason: reason || "" } : u));
  }

  async function deleteUser(userId: string, userName: string) {
    const confirm1 = window.confirm(`Tem certeza que deseja excluir o usuário "${userName}"?

Esta ação é irreversível e removerá o perfil, anúncios e dados do usuário.`);
    if (!confirm1) return;
    const confirm2 = window.confirm(`CONFIRMAÇÃO FINAL: Excluir "${userName}" permanentemente?`);
    if (!confirm2) return;
    // Excluir anúncios do usuário
    await supabase.from("listings").delete().eq("owner_id", userId);
    // Excluir perfil
    await supabase.from("profiles").delete().eq("id", userId);
    // Remover da lista local
    setUsers((prev) => prev.filter((u) => u.id !== userId));
    alert(`Usuário "${userName}" excluído com sucesso.`);
  }

  async function sendAdminMessage(userEmail: string, userName: string) {
    if (!userEmail) {
      alert("Este usuário não possui e-mail cadastrado.");
      return;
    }
    const message = prompt(`Mensagem para ${userName}:`);
    if (!message) return;
    const { error } = await supabase.functions.invoke("notify-new-message", {
      body: {
        recipient_email: userEmail,
        recipient_name: userName,
        sender_name: "Equipe AgroAluga",
        listing_title: "Mensagem da Administração",
        message_preview: message,
      },
    });
    if (error) {
      alert("Erro ao enviar mensagem. Tente novamente.");
      console.error("sendAdminMessage error:", error);
    } else {
      alert(`Mensagem enviada para ${userEmail}!`);
    }
  }

  async function approve(id: string) {
    setProcessing(id);
    const listing = listings.find((l) => l.id === id);
    await supabase.from("listings").update({ status: "active" }).eq("id", id);
    if (listing) {
      await supabase.functions.invoke("notify-listing-status", {
        body: { listing_title: listing.title, owner_email: listing.owner_email, owner_name: listing.owner_name, owner_id: listing.owner_id, owner_listing_id: listing.id, status: "active", reason: "" },
      });
    }
    setListings((prev) => prev.filter((l) => l.id !== id));
    if (selected?.id === id) setSelected(null);
    setProcessing(null);
  }

  async function reject(id: string) {
    const listing = listings.find((l) => l.id === id);
    if (listing) {
      setRejectModal({ id, title: listing.title });
      setRejectReason("");
    }
  }
  async function confirmReject() {
    if (!rejectModal) return;
    const { id } = rejectModal;
    setProcessing(id);
    const listing = listings.find((l) => l.id === id);
    await supabase.from("listings").update({ status: "rejected" }).eq("id", id);
    if (listing) {
      await supabase.functions.invoke("notify-listing-status", {
        body: { listing_title: listing.title, owner_email: listing.owner_email, owner_name: listing.owner_name, owner_id: listing.owner_id, owner_listing_id: listing.id, status: "rejected", reason: rejectReason },
      });
    }
    // Só remove da lista se não está na aba rejeitados
    if (statusFilter !== "rejected") {
      setListings((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
    } else {
      // Atualiza o reason no selected
      setSelected((prev) => prev?.id === id ? { ...prev, rejection_reason: rejectReason } : prev);
    }
    setProcessing(null);
    setRejectModal(null);
    setRejectReason("");
  }

  async function changeListingStatus(id: string, newStatus: string) {
    if (newStatus === "rejected") {
      const listing = listings.find((l) => l.id === id);
      if (listing) { setRejectModal({ id, title: listing.title }); setRejectReason(""); }
      return;
    }
    setProcessing(id);
    await supabase.from("listings").update({ status: newStatus }).eq("id", id);
    setListings((prev) => prev.filter((l) => l.id !== id));
    if (selected?.id === id) setSelected(null);
    setProcessing(null);
    fetchPendingCount();
  }

  async function deleteListing(id: string) {
    if (!confirm("Tem certeza que deseja DELETAR este anúncio permanentemente?")) return;
    setProcessing(id);
    await supabase.from("listings").delete().eq("id", id);
    setListings((prev) => prev.filter((l) => l.id !== id));
    if (selected?.id === id) setSelected(null);
    setProcessing(null);
  }

  async function adminSetPlan(userId: string, planId: string, justification?: string) {
    const params: any = { p_user_id: userId, p_plan_id: planId };
    if (justification) params.p_justification = justification;
    const { data, error } = await supabase.rpc("admin_set_plan", params);
    if ((data as any)?.blocked) {
      // assinatura Stripe ativa: abre modal pedindo justificativa (bloqueio legal)
      setPlanBlockModal({ userId, planId });
      setPlanJustification("");
      return;
    }
    if (error || !(data as any)?.ok) {
      alert("Erro ao alterar plano: " + (error?.message || (data as any)?.error || "desconhecido"));
      return;
    }
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, plan_id: planId } as any : u));
    setPlanSaved(userId);
    setPlanBlockModal(null);
    setTimeout(() => setPlanSaved((s) => (s === userId ? null : s)), 2000);
  }
  function confirmPlanDowngrade() {
    if (!planBlockModal || planJustification.trim().length < 5) return;
    adminSetPlan(planBlockModal.userId, planBlockModal.planId, planJustification.trim());
  }

  async function toggleAdmin(userId: string, current: boolean) {
    await supabase.from("profiles").update({ is_admin: !current }).eq("id", userId);
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_admin: !current } : u));
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleSelectAll(ids: string[]) {
    setSelectedIds((prev) => {
      const allSelected = ids.every((i) => prev.has(i));
      return allSelected ? new Set() : new Set(ids);
    });
  }
  async function bulkApprove() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Aprovar ${selectedIds.size} anúncio(s)?`)) return;
    const { data, error } = await supabase.rpc("admin_bulk_approve", { p_ids: Array.from(selectedIds) });
    if (error || !(data as any)?.ok) { alert("Erro: " + (error?.message || (data as any)?.error)); return; }
    setSelectedIds(new Set()); fetchListings(); fetchPendingCount();
  }
  async function bulkDelete() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Excluir permanentemente ${selectedIds.size} anúncio(s)? Esta ação é irreversível.`)) return;
    const { data, error } = await supabase.rpc("admin_bulk_delete", { p_ids: Array.from(selectedIds) });
    if (error || !(data as any)?.ok) { alert("Erro: " + (error?.message || (data as any)?.error)); return; }
    setSelectedIds(new Set()); fetchListings(); fetchPendingCount();
  }
  async function bulkReject() {
    if (selectedIds.size === 0 || !bulkRejectMsg.trim()) return;
    const { data, error } = await supabase.rpc("admin_bulk_reject", { p_ids: Array.from(selectedIds), p_reason: bulkRejectMsg });
    if (error || !(data as any)?.ok) { alert("Erro: " + (error?.message || (data as any)?.error)); return; }
    setSelectedIds(new Set()); setBulkRejectOpen(false); setBulkRejectMsg(""); fetchListings(); fetchPendingCount();
  }

  function withinPeriod(dateStr: string) {
    if (exportDays === "all") return true;
    const d = new Date(dateStr).getTime();
    return d > Date.now() - Number(exportDays) * 86400000;
  }
  function exportUsers() {
    const rows = users.filter((u: any) => withinPeriod(u.created_at)).map((u: any) => ({
      "Nome": u.name, "E-mail": u.email, "Telefone": u.phone || "",
      "Cidade": u.city || "", "Estado": u.state || "",
      "Código": u.user_code || "", "Plano": u.plan_id || "free",
      "Ciclo": (u as any).billing_cycle || "—",
      "Via Stripe": (u as any).has_stripe ? "Sim" : "Não",
      "Renova em (dias)": (u as any).days_left ?? "—",
      "Cancelando": (u as any).cancel_at_period_end ? "Sim" : "Não",
      "Anúncios usados": (u as any).listings_used ?? 0,
      "Anúncios limite": ((u as any).listings_limit === -1 ? "Ilimitado" : (u as any).listings_limit ?? 3),
      "Destaques usados": (u as any).featured_used ?? 0,
      "Destaques limite": ((u as any).featured_limit === -1 ? "Ilimitado" : (u as any).featured_limit ?? 0),
      "Admin": u.is_admin ? "Sim" : "Não", "Bloqueado": u.blocked ? "Sim" : "Não",
      "Total anúncios": u.listing_count || 0,
      "Cadastro": new Date(u.created_at).toLocaleDateString("pt-BR"),
    }));
    if (rows.length === 0) { alert("Nenhum usuário no período."); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Usuarios");
    XLSX.writeFile(wb, `usuarios-${exportDays === "all" ? "completo" : exportDays + "dias"}.xlsx`);
  }
  function exportListings() {
    const rows = filteredListings.filter((l: any) => withinPeriod(l.created_at)).map((l: any) => ({
      "Código": l.ticket_number ? "ANC-" + l.ticket_number : "",
      "Título": l.title, "Categoria": l.category, "Tipo": l.listing_type,
      "Preço": l.price, "Unidade": l.price_unit,
      "Cidade": l.city, "Estado": l.state,
      "Anunciante": l.owner_name, "ID Anunciante": l.owner?.user_code || "", "E-mail": l.owner_email,
      "Status": l.status, "Views": l.views || 0,
      "Criado": new Date(l.created_at).toLocaleDateString("pt-BR"),
    }));
    if (rows.length === 0) { alert("Nenhum anúncio no período."); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Anuncios");
    XLSX.writeFile(wb, `anuncios-${statusFilter}-${exportDays === "all" ? "completo" : exportDays + "dias"}.xlsx`);
  }

  const filteredListings = listings.filter((l) => {
    const matchUser = userFilter === "" || l.owner_name.toLowerCase().includes(userFilter.toLowerCase()) || l.owner_email?.toLowerCase().includes(userFilter.toLowerCase());
    const matchCity = cityFilter === "" || l.city?.toLowerCase().includes(cityFilter.toLowerCase());
    const matchCategory = categoryFilter === "all" || l.category === categoryFilter;
    if (!matchCity || !matchCategory) return false;
    const matchType = typeFilter === "all" || l.listing_type === typeFilter;
    return matchUser && matchType;
  }).sort((a, b) => {
    if (listingSort === "oldest") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    if (listingSort === "price_asc") return a.price - b.price;
    if (listingSort === "price_desc") return b.price - a.price;
    if (listingSort === "city") return (a.city || "").localeCompare(b.city || "");
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime(); // newest
  });

  const filteredUsers = users
    .filter((u) =>
      userSearch === "" ||
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.user_code?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.city?.toLowerCase().includes(userSearch.toLowerCase())
    )
    .sort((a, b) => {
      if (userSort === "alpha") return (a.name || "").localeCompare(b.name || "");
      if (userSort === "oldest") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (userSort === "listings") return (b.listing_count || 0) - (a.listing_count || 0);
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime(); // newest
    });

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;
  if (isAdmin === false) return <Navigate to="/" />;
  if (isAdmin === null) return <div className="container mx-auto px-4 py-20 text-center text-muted-foreground">Verificando permissões...</div>;

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl overflow-x-hidden">
      <div className="agro-header-v2 mb-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[25px] font-bold leading-tight tracking-tight text-[#1a4a2e]">Painel Admin</h1>
            <p className="mt-0.5 text-[13px] capitalize text-[#8a9088]">
              {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div className="flex items-center gap-2">
          <Link to="/admin/relatorios" className="inline-flex items-center gap-1.5 rounded-full border border-[#ebe7dc] bg-white px-3 py-1.5 text-xs font-semibold text-[#1a4a2e] transition hover:border-[#2d7a4d] hover:bg-[#eaf2ec]">
            📊 Relatórios
          </Link>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#eaf2ec] px-3 py-1.5 text-xs font-semibold text-[#2d7a4d]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2d7a4d] opacity-60 motion-reduce:hidden" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2d7a4d]" />
            </span>
            Plataforma no ar
          </span>
          </div>
        </div>

        <div role="tablist" className="flex gap-1 overflow-x-auto rounded-xl bg-[#ece8dd] p-1.5 [scrollbar-width:none]">
          {([
            { id: "overview", label: "Visão Geral", Icon: LayoutDashboard, badge: 0, alert: false },
            { id: "listings", label: "Anúncios", Icon: LayoutList, badge: pendingCount, alert: true },
            { id: "users", label: "Usuários", Icon: Users, badge: users.length, alert: false },
            { id: "affiliates", label: "Afiliados", Icon: UserPlus, badge: 0, alert: false },
            { id: "stores", label: "Lojas", Icon: Store, badge: stores.length, alert: false },
          ] as const).map(({ id, label, Icon, badge, alert }) => {
            const on = activeTab === id;
            return (
              <button key={id} role="tab" aria-selected={on} onClick={() => setActiveTab(id)}
                className={`flex min-w-max flex-1 items-center justify-center gap-1.5 rounded-[9px] px-3 py-2.5 text-[13px] font-semibold transition-all sm:text-sm ${on ? "bg-white text-[#1a4a2e] shadow-[0_1px_3px_rgba(26,74,46,0.12)]" : "text-[#8a9088] hover:text-[#1a4a2e]"}`}>
                <Icon className="h-4 w-4 shrink-0" />
                {label}
                {badge > 0 && (
                  <span className={`rounded-full px-1.5 py-px text-[11px] font-bold leading-4 ${alert ? "bg-[#c0493b] text-white" : "bg-[#eaf2ec] text-[#2d7a4d]"}`}>{badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ABA VISAO GERAL */}
      {activeTab === "overview" && (
        kpis ? (
          <AdminOverview
            kpis={kpis}
            growth={growthData.map((d: any) => ({ label: d.day, users: Number(d.new_users) || 0, listings: Number(d.new_listings) || 0 }))}
            periodControl={
              <div className="flex gap-1 rounded-lg bg-[#f3f0e8] p-1">
                {[{ v: 7, l: "7d" }, { v: 30, l: "30d" }, { v: 90, l: "90d" }, { v: 365, l: "1 ano" }].map((p) => (
                  <button key={p.v} onClick={() => setGrowthPeriod(p.v)}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${growthPeriod === p.v ? "bg-[#1a4a2e] text-white" : "text-[#8a9088] hover:bg-[#eaf2ec] hover:text-[#2d7a4d]"}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            }
            pending={listings.filter((l) => l.status === "pending")}
            pendingLoading={listingsLoading}
            onApprove={async (id) => { await approve(id); fetchKpis(); fetchPendingCount(); }}
            onReject={(id) => reject(id)}
            onGoTo={(tab, status) => { const p = new URLSearchParams(searchParams); p.set("tab", tab); if (status) p.set("status", status); setSearchParams(p); }}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{kpisLoading ? "Carregando indicadores..." : "Não foi possível carregar os indicadores."}</p>
        )
      )}

            {/* ABA ANÚNCIOS */}
      {activeTab === "listings" && (
        <div>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <select value={exportDays} onChange={(e) => setExportDays(e.target.value)} className="h-9 text-xs rounded-md border border-border bg-background px-2">
              <option value="30">Último mês</option>
              <option value="60">2 meses</option>
              <option value="90">3 meses</option>
              <option value="365">1 ano</option>
              <option value="all">Completo</option>
            </select>
            <Button size="sm" variant="outline" onClick={exportListings} className="text-xs">Exportar XLSX</Button>
            <span className="text-xs text-muted-foreground">(exporta a sub-aba atual)</span>
          </div>
          {/* Filtros */}
          <div className="flex flex-wrap gap-3 mb-6 bg-card border border-border rounded-lg p-4">
            <div className="flex gap-2 flex-wrap">
              {(["pending", "active", "paused", "rejected", "expired"] as const).map((s) => (
                <Button key={s} variant={statusFilter === s ? "default" : "outline"} size="sm"
                  onClick={() => { setStatusFilter(s); setSelected(null); setSelectedIds(new Set()); setExpandedId(null); }}
                  className={statusFilter === s ? "bg-primary text-primary-foreground" : ""}>
                  {s === "pending" ? "Pendentes" : s === "active" ? "Ativos" : s === "paused" ? "Pausados" : s === "rejected" ? "Rejeitados" : "Expirados"}
                  {s === "pending" && pendingCount > 0 && statusFilter !== "pending" && (
                    <span className="ml-1 bg-accent text-accent-foreground text-xs px-1.5 rounded-full">{pendingCount}</span>
                  )}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 ml-auto">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="equipamento">Equipamentos</SelectItem>
                  <SelectItem value="servico">Servicos</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Buscar por usuário..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="pl-7 h-8 text-xs w-48"
                />
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Filtrar por cidade..."
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="pl-7 h-8 text-xs w-40"
                />
              </div>
              <select
                value={listingSort}
                onChange={(e) => setListingSort(e.target.value as any)}
                className="flex h-8 rounded-md border border-input bg-background px-2 py-1 text-xs"
              >
                <option value="newest">Mais novos</option>
                <option value="oldest">Mais antigos</option>
                <option value="price_asc">Menor preço</option>
                <option value="price_desc">Maior preço</option>
                <option value="city">Cidade A→Z</option>
              </select>
            </div>
          </div>

          <div>
            {/* Barra de seleção em massa */}
            {selectedIds.size > 0 && (
              <div className="sticky top-2 z-20 mb-3 flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 shadow-lg">
                <span className="text-sm font-medium">{selectedIds.size} selecionado(s)</span>
                <div className="flex gap-2 ml-auto">
                  {(statusFilter === "pending" || statusFilter === "paused") && (
                    <button onClick={bulkApprove} className="text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-md">✓ Aprovar</button>
                  )}
                  {statusFilter !== "rejected" && statusFilter !== "expired" && (
                    <button onClick={() => setBulkRejectOpen(true)} className="text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-md">✕ Rejeitar</button>
                  )}
                  <button onClick={bulkDelete} className="text-xs font-semibold bg-red-500/80 hover:bg-red-500 px-3 py-1.5 rounded-md">🗑 Excluir</button>
                  <button onClick={() => setSelectedIds(new Set())} className="text-xs px-2 py-1.5 rounded-md hover:bg-white/20">Limpar</button>
                </div>
              </div>
            )}
            {/* Selecionar todos */}
            {!listingsLoading && filteredListings.length > 0 && (
              <label className="flex items-center gap-2 mb-2 px-1 text-xs text-muted-foreground cursor-pointer select-none">
                <input type="checkbox"
                  checked={filteredListings.every((l) => selectedIds.has(l.id))}
                  onChange={() => toggleSelectAll(filteredListings.map((l) => l.id))}
                  className="h-4 w-4 accent-primary" />
                Selecionar todos ({filteredListings.length})
              </label>
            )}
            {/* Lista */}
            <div className="space-y-3">
              {listingsLoading ? (
                <p className="text-muted-foreground text-sm">Carregando...</p>
              ) : filteredListings.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
                  Nenhum anúncio encontrado.
                </div>
              ) : filteredListings.map((l) => (
                <div key={l.id}
                  className={`bg-card border rounded-lg p-4 cursor-pointer transition-all ${selected?.id === l.id ? "border-primary shadow-md" : "border-border hover:border-primary/50"}`}
                  onClick={async () => {
                    if (selected?.id === l.id) { setSelected(null); return; }
                    if (l.status === "rejected") {
                      const { data } = await supabase.from("notifications").select("id, message").ilike("link", `%${l.id}%`).order("created_at", { ascending: false }).limit(1);
                      const reason = data?.[0]?.message?.replace(/^.*?Motivo: /, "") || "";
                      console.log("reason:", reason);
                      setSelected({ ...l, rejection_reason: reason });
                    } else {
                      console.log("selected:", l.status, l.id);
                      setSelected(l);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(l.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(l.id)}
                      className="h-4 w-4 accent-primary shrink-0"
                    />
                    <img src={l.images?.[0] || "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=80&h=80&fit=crop"}
                      alt="" className="h-14 w-14 rounded-md object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{l.title}</p>
                      {(l as any).ticket_number && <span className="text-[10px] font-mono text-muted-foreground">ANC-{(l as any).ticket_number}</span>}
                      {isPromoted(l.id) && (
                        <span className="ml-1 inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          <ArrowUp className="h-2.5 w-2.5" /> Promovido
                        </span>
                      )}
                      <p className="text-xs text-muted-foreground">{l.category} — {l.city}, {l.state}</p>
                      <p className="text-xs text-muted-foreground">Por: {l.owner_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${l.listing_type === "servico" ? "bg-accent/20 text-accent-foreground" : "bg-primary/10 text-primary"}`}>
                          {l.listing_type === "servico" ? "Servico" : "Equipamento"}
                        </span>
                        <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString("pt-BR")}</span>
                      </div>
                    </div>
                    {statusFilter === "pending" && (
                      <div className="flex gap-1">
                        <Button size="icon" variant="outline" className="h-8 w-8 text-green-600 hover:bg-green-50"
                          onClick={(e) => { e.stopPropagation(); approve(l.id); }} disabled={processing === l.id}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          onClick={(e) => { e.stopPropagation(); reject(l.id); }} disabled={processing === l.id}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Detalhes expandidos */}
                  {selected?.id === l.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        {l.images?.map((img, i) => (
                          <img key={i} src={img} alt="" className="h-20 w-20 rounded-md object-cover" />
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <p><span className="font-medium">Tipo:</span> {l.listing_type === "servico" ? "Servico" : "Equipamento"}</p>
                        <p><span className="font-medium">Preço:</span> R$ {l.price}/{l.price_unit}</p>
                        <p><span className="font-medium">Local:</span> {l.city}, {l.state}</p>
                        <p className="col-span-2"><span className="font-medium">Anunciante:</span> {l.owner_name} — {l.owner_email}</p>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{l.description}</p>
                      {l.status === "rejected" && selected?.id === l.id && selected?.rejection_reason && (
                        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-semibold text-destructive">Motivo da rejeição:</p>
                            <button
                              onClick={() => { setEditRejectModal({ id: "", listingId: l.id, message: selected?.rejection_reason || "" }); setEditRejectMsg(selected?.rejection_reason || ""); }}
                              className="text-xs text-primary hover:underline"
                            >
                              Editar
                            </button>
                          </div>
                          <p className="text-xs text-foreground/80">{l.rejection_reason}</p>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 pt-2">
                        {l.status !== "active" && (
                          <Button size="sm" className="gap-1 text-xs bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => approve(l.id)} disabled={processing === l.id}>
                            <Check className="h-3 w-3" /> Aprovar
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="gap-1 text-xs text-destructive"
                          onClick={() => reject(l.id)} disabled={processing === l.id}>
                          <X className="h-3 w-3" /> Rejeitar
                        </Button>
                        <a href={`/anuncio/${l.id}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="gap-1 text-xs">Ver anúncio</Button>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>
      )}

      {/* ABA USUÁRIOS */}
      {activeTab === "users" && (
        <div>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <select value={exportDays} onChange={(e) => setExportDays(e.target.value)} className="h-9 text-xs rounded-md border border-border bg-background px-2">
              <option value="30">Último mês</option>
              <option value="60">2 meses</option>
              <option value="90">3 meses</option>
              <option value="365">1 ano</option>
              <option value="all">Completo</option>
            </select>
            <Button size="sm" variant="outline" onClick={exportUsers} className="text-xs">Exportar XLSX</Button>
          </div>
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, código ou cidade..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              value={userSort}
              onChange={(e) => setUserSort(e.target.value as any)}
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="newest">Mais novos</option>
              <option value="oldest">Mais antigos</option>
              <option value="alpha">A → Z</option>
              <option value="listings">Mais anúncios</option>
            </select>
            <p className="text-sm text-muted-foreground self-center">{filteredUsers.length} usuário(s)</p>
          </div>

          {/* Mobile: cards */}
          <div className="md:hidden flex flex-col gap-3">
            {usersLoading ? (
              <p className="text-center text-muted-foreground py-8">Carregando...</p>
            ) : filteredUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum usuário encontrado.</p>
            ) : filteredUsers.map((u) => (
              <div key={u.id} className={`bg-card border border-border rounded-lg p-4 ${(u as any).blocked ? "border-destructive/30 bg-destructive/5" : ""}`}>
                {/* Cabeçalho do card */}
                <div className="flex items-center gap-3 mb-3">
                  <UserAvatar userId={u.id} name={u.name || ""} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-semibold text-sm">{u.name}</p>
                      {(u as any).blocked && <span className="text-xs bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full">Bloqueado</span>}
                      {u.is_admin && <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">Admin</span>}
                    </div>
                    <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-0.5 rounded mt-0.5 inline-block">
                      {u.user_code || "—"}
                    </span>
                  </div>
                </div>
                {/* Detalhes */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
                  <div><span className="font-medium text-foreground">Tel:</span> {formatPhone(u.phone) || "—"}</div>
                  <div><span className="font-medium text-foreground">Local:</span> {u.city && u.state ? `${u.city}, ${u.state}` : "—"}</div>
                  <div className="col-span-2 truncate"><span className="font-medium text-foreground">E-mail:</span> {(u as any).email || "—"}</div>
                  <div><span className="font-medium text-foreground">Cadastro:</span> {new Date(u.created_at).toLocaleDateString("pt-BR")}</div>
                  {(u as any).blocked && (u as any).block_reason && (
                    <div className="col-span-2 text-destructive"><span className="font-medium">Motivo:</span> {(u as any).block_reason}</div>
                  )}
                </div>
                {/* Plano & Uso */}
                {!u.is_admin && (() => {
                  const a = u as any;
                  const ll = a.listings_limit ?? 3, fl = a.featured_limit ?? 0;
                  const lu = a.listings_used ?? 0, fu = a.featured_used ?? 0;
                  return (
                    <div className="bg-secondary/40 rounded-md px-3 py-2 mb-3 text-xs flex flex-wrap gap-x-4 gap-y-1">
                      <span className="font-medium capitalize">{a.plan_id || "free"}{a.has_stripe && " · Stripe"}</span>
                      <span>📋 {lu}/{ll === -1 ? "∞" : ll}</span>
                      <span>⭐ {fu}/{fl === -1 ? "∞" : fl}</span>
                      {a.days_left != null && <span>⏱ {a.days_left}d{a.cancel_at_period_end && " (cancelando)"}</span>}
                    </div>
                  );
                })()}
                {/* Ações */}
                {u.id === user.id ? (
                  <span className="text-xs text-muted-foreground">Você</span>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => sendAdminMessage((u as any).email, u.name)}
                      className="text-xs px-3 py-1.5 rounded-md font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                      Mensagem
                    </button>
                    <button onClick={() => toggleBlock(u.id, (u as any).blocked)}
                      className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${(u as any).blocked ? "bg-green-100 text-green-700" : "bg-destructive/10 text-destructive"}`}>
                      {(u as any).blocked ? "Desbloquear" : "Bloquear"}
                    </button>
                    <button onClick={() => toggleAdmin(u.id, u.is_admin)}
                      className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${u.is_admin ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {u.is_admin ? "Admin ✓" : "+ Admin"}
                    </button>
                    <select
                      value={(u as any).plan_id || "free"}
                      onChange={(e) => adminSetPlan(u.id, e.target.value)}
                      className="text-xs px-2 py-1.5 rounded-md font-medium border border-border bg-card"
                      title="Plano do usuário"
                    >
                      <option value="free">Gratuito</option>
                      <option value="plus">Plus</option>
                      <option value="pro">Pro</option>
                      <option value="ultra">Ultra</option>
                    </select>
                    {planSaved === u.id && (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">✓ salvo</span>
                    )}
                    <button onClick={() => deleteUser(u.id, u.name)}
                      className="text-xs px-3 py-1.5 rounded-md font-medium bg-destructive/10 text-destructive hover:bg-destructive hover:text-white transition-colors">
                      Excluir
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left p-3 font-medium text-muted-foreground">Usuário</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Código</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">E-mail</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Localização</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Cadastro</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Plano &amp; Uso</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Carregando...</td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhum usuário encontrado.</td></tr>
                ) : filteredUsers.map((u, i) => (
                  <tr key={u.id} className={`border-t border-border hover:bg-secondary/50 transition-colors ${(u as any).blocked ? "bg-destructive/5" : i % 2 === 0 ? "" : "bg-secondary/20"}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar userId={u.id} name={u.name || ""} size="sm" />
                        <div>
                          <div className="flex items-center gap-1 flex-wrap">
                            <p className="font-medium">{u.name}</p>
                            {(u as any).blocked && <span className="text-xs bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full">Bloqueado</span>}
                            {u.is_admin && <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">Admin</span>}
                          </div>
                          <p className="text-xs text-muted-foreground">{formatPhone(u.phone) || "—"}</p>
                          {(u as any).blocked && (u as any).block_reason && (
                            <p className="text-xs text-destructive">Motivo: {(u as any).block_reason}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {u.user_code || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground text-xs">{(u as any).email || "—"}</td>
                    <td className="p-3 text-muted-foreground text-xs">{u.city && u.state ? `${u.city}, ${u.state}` : "—"}</td>
                    <td className="p-3 text-muted-foreground text-xs">{new Date(u.created_at).toLocaleDateString("pt-BR")}</td>
                    <td className="p-3 text-xs">
                      {u.is_admin ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (() => {
                        const a = u as any;
                        const ll = a.listings_limit ?? 3, fl = a.featured_limit ?? 0;
                        const lu = a.listings_used ?? 0, fu = a.featured_used ?? 0;
                        const near = (used: number, lim: number) => lim !== -1 && lim > 0 && used >= lim;
                        return (
                          <div className="space-y-0.5 min-w-[130px]">
                            <div className="font-medium capitalize">{a.plan_id || "free"}{a.has_stripe && <span className="ml-1 text-[9px] bg-primary/10 text-primary px-1 py-0.5 rounded">Stripe</span>}</div>
                            <div className={near(lu, ll) ? "text-destructive" : "text-muted-foreground"}>
                              📋 {lu}/{ll === -1 ? "∞" : ll} anúncios
                            </div>
                            <div className={near(fu, fl) ? "text-destructive" : "text-muted-foreground"}>
                              ⭐ {fu}/{fl === -1 ? "∞" : fl} destaques
                            </div>
                            {a.days_left != null && (
                              <div className={a.days_left <= 3 ? "text-amber-600" : "text-muted-foreground"}>
                                ⏱ renova em {a.days_left}d{a.cancel_at_period_end && " (cancelando)"}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3">
                      {u.id === user.id ? (
                        <span className="text-xs text-muted-foreground">Você</span>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          <button onClick={() => sendAdminMessage((u as any).email, u.name)}
                            className="text-xs px-2 py-1 rounded font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Mensagem
                          </button>
                          <button onClick={() => toggleBlock(u.id, (u as any).blocked)}
                            className={`text-xs px-2 py-1 rounded font-medium transition-colors ${(u as any).blocked ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-destructive/10 text-destructive hover:bg-destructive/20"}`}>
                            {(u as any).blocked ? "Desbloquear" : "Bloquear"}
                          </button>
                          <button onClick={() => toggleAdmin(u.id, u.is_admin)}
                            className={`text-xs px-2 py-1 rounded font-medium transition-colors ${u.is_admin ? "bg-primary/10 text-primary hover:bg-destructive/10 hover:text-destructive" : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"}`}>
                            {u.is_admin ? "Admin ✓" : "+ Admin"}
                          </button>
                          <select
                            value={(u as any).plan_id || "free"}
                            onChange={(e) => adminSetPlan(u.id, e.target.value)}
                            className="text-xs px-2 py-1 rounded font-medium border border-border bg-card"
                            title="Plano do usuário">
                            <option value="free">Gratuito</option>
                            <option value="plus">Plus</option>
                            <option value="pro">Pro</option>
                            <option value="ultra">Ultra</option>
                          </select>
                          {planSaved === u.id && (
                            <span className="text-xs text-green-600 font-medium flex items-center gap-1">✓ salvo</span>
                          )}
                          {u.id !== user?.id && (
                            <button onClick={() => deleteUser(u.id, u.name)}
                              className="text-xs px-2 py-1 rounded font-medium bg-destructive/10 text-destructive hover:bg-destructive hover:text-white transition-colors">
                              Excluir
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ABA AFILIADOS */}
      {activeTab === "affiliates" && (
        <div>
          <AffiliatesTab />
        </div>
      )}

      {/* ABA LOJAS */}
      {activeTab === "stores" && (
        <div className="agro-stores-v2 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-base font-bold text-[#1a4a2e]">
              <Store className="h-[18px] w-[18px] text-[#2d7a4d]" /> Lojas ativas
              <span className="font-medium text-[#8a9088]">({stores.length})</span>
            </h2>
            <button onClick={loadStores} disabled={storesLoading}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-[#2d7a4d] transition hover:bg-[#eaf2ec] disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${storesLoading ? "animate-spin" : ""}`} /> Atualizar
            </button>
          </div>

          {!storesLoading && stores.length > 0 && (
            <BreakdownGrid cols={4} items={[
              { label: "Lojas ativas", value: stores.length },
              { label: "Ultra", value: stores.filter((x: any) => x.plan_id === "ultra").length, tone: "star" },
              { label: "Pro", value: stores.filter((x: any) => x.plan_id === "pro").length, tone: "ok" },
              { label: "Anúncios nas lojas", value: stores.reduce((a: number, x: any) => a + Number(x.total_ads || 0), 0), tone: "info" },
            ]} />
          )}

          {storesLoading ? (
            <div className="space-y-2.5">
              {[0, 1, 2].map((i) => <div key={i} className="h-[74px] animate-pulse rounded-2xl border border-[#ebe7dc] bg-white" />)}
            </div>
          ) : stores.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-[#ebe7dc] bg-white py-12 text-center">
              <Store className="h-8 w-8 text-[#2d7a4d]" />
              <div className="font-semibold text-[#1a4a2e]">Nenhuma loja ativa</div>
              <div className="text-sm text-[#8a9088]">Quando um assinante Pro ou Ultra ativar a Minha Loja, ela aparece aqui.</div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {stores.map((st: any) => {
                const plan = String(st.plan_id || "free").toLowerCase();
                const planCls = plan === "ultra" ? "bg-[#fbf1dc] text-[#c98a1a]" : plan === "pro" ? "bg-[#eaf2ec] text-[#2d7a4d]" : "bg-[#f0ede4] text-[#8a9088]";
                return (
                  <div key={st.owner_id}
                    className="flex flex-col gap-3 rounded-2xl border border-[#ebe7dc] bg-white p-3.5 transition-colors hover:border-[#2d7a4d] sm:flex-row sm:items-center sm:px-4">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#eaf2ec] text-[#2d7a4d]">
                        {st.avatar_url ? <img src={st.avatar_url} alt="" className="h-full w-full object-cover" /> : <Store className="h-[22px] w-[22px]" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-[15px] font-semibold text-[#1f2621]">{st.store_name || st.name}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${planCls}`}>{plan}</span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-[#8a9088]">
                          {st.store_slug
                            ? <a href={"/loja/" + st.store_slug} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-[#2d7a4d] hover:underline">/loja/{st.store_slug}<ExternalLink className="h-3 w-3" /></a>
                            : <span className="italic">sem nome definido</span>}
                          <span>·</span>
                          {st.avg_rating != null
                            ? <span><span className="font-semibold text-[#e0a021]">★ {Number(st.avg_rating).toFixed(1).replace(".", ",")}</span> ({st.total_reviews})</span>
                            : <span>sem avaliações</span>}
                          <span>·</span>
                          <span>{st.total_ads} anúncios</span>
                        </div>
                        <div className="text-[11.5px] text-[#a3a8a1]">{st.user_code}{st.city ? " · " + st.city + "/" + st.state : ""}</div>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {st.store_slug && (
                        <button onClick={() => adminResetSlug(st.owner_id)}
                          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#ebe7dc] bg-white px-3 py-2 text-[13px] font-semibold text-[#1f2621] transition hover:border-[#2d7a4d] hover:bg-[#eaf2ec] hover:text-[#1a4a2e] sm:flex-none">
                          <Lock className="h-3.5 w-3.5" /> Liberar nome
                        </button>
                      )}
                      <button onClick={() => adminToggleStore(st.owner_id, st.store_enabled)}
                        className="inline-flex flex-1 items-center justify-center rounded-lg border border-[#eccbc6] bg-white px-3 py-2 text-[13px] font-semibold text-[#c0493b] transition hover:border-[#c0493b] hover:bg-[#f7e7e4] sm:flex-none">
                        Desativar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 border-t border-[#ebe7dc] py-4 text-center text-xs text-[#8a9088]">
        AgroAluga Admin · v1.0.1
      </div>

      {/* Modal de justificativa para downgrade com Stripe ativo */}
      {planBlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPlanBlockModal(null)}>
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg mb-2">⚠️ Assinatura ativa no Stripe</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Este usuário tem uma assinatura ativa no Stripe. Reduzir o plano aqui <strong>não cancela a cobrança</strong> — o ideal é cancelar pelo Stripe primeiro.
              Para reduzir mesmo assim (ex: bloqueio legal, fraude, ordem judicial), informe uma justificativa. Ela será registrada e enviada ao usuário.
            </p>
            <label className="text-xs text-muted-foreground">Justificativa (mínimo 5 caracteres)</label>
            <textarea
              value={planJustification}
              onChange={(e) => setPlanJustification(e.target.value)}
              rows={3}
              placeholder="Ex: Bloqueio por violação dos termos de uso (processo nº...)"
              className="w-full mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPlanBlockModal(null)}
                className="flex-1 px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors">
                Cancelar
              </button>
              <button onClick={confirmPlanDowngrade}
                disabled={planJustification.trim().length < 5}
                className="flex-1 px-4 py-2 rounded-md bg-destructive text-white text-sm font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50">
                Reduzir com justificativa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rejeição */}
      {bulkRejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setBulkRejectOpen(false)}>
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-heading font-bold mb-1">Rejeitar {selectedIds.size} anúncio(s)</h3>
            <p className="text-sm text-muted-foreground mb-4">O motivo será enviado a todos os anunciantes selecionados.</p>
            <textarea
              value={bulkRejectMsg}
              onChange={(e) => setBulkRejectMsg(e.target.value)}
              placeholder="Motivo da rejeição (ex: fotos de baixa qualidade, informações incompletas...)"
              rows={4}
              className="w-full border border-border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setBulkRejectOpen(false); setBulkRejectMsg(""); }}
                className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary">
                Cancelar
              </button>
              <button onClick={bulkReject} disabled={!bulkRejectMsg.trim()}
                className="px-4 py-2 text-sm rounded-md bg-destructive text-white font-medium hover:bg-destructive/90 disabled:opacity-50">
                Rejeitar {selectedIds.size}
              </button>
            </div>
          </div>
        </div>
      )}
      {editRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-1">Editar motivo de rejeição</h3>
            <p className="text-sm text-muted-foreground mb-4">A nova mensagem será salva e o usuário será notificado.</p>
            <textarea
              className="w-full border border-border rounded-md px-3 py-2 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              value={editRejectMsg}
              onChange={(e) => setEditRejectMsg(e.target.value)}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setEditRejectModal(null)}
                className="flex-1 px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!editRejectModal || !editRejectMsg.trim()) return;
                  // Atualizar notificação no banco
                  const notifMessage = `Seu anuncio foi rejeitado. Motivo: ${editRejectMsg}`;
                  await supabase.from("notifications")
                    .update({ message: notifMessage })
                    .eq("user_id", selected?.owner_id)
                    .ilike("link", `%${editRejectModal.listingId}%`);
                  // Atualizar selected
                  setSelected((prev) => prev ? { ...prev, rejection_reason: editRejectMsg } : prev);
                  setEditRejectModal(null);
                }}
                disabled={!editRejectMsg.trim()}
                className="flex-1 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-1">Rejeitar anúncio</h3>
            <p className="text-sm text-muted-foreground mb-4">"{rejectModal.title}"</p>
            <label className="text-sm font-medium block mb-2">Justificativa <span className="text-destructive">*</span></label>
            <textarea
              className="w-full border border-border rounded-md px-3 py-2 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Explique o motivo da rejeição para o anunciante..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setRejectModal(null); setRejectReason(""); }}
                className="flex-1 px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason.trim() || processing === rejectModal.id}
                className="flex-1 px-4 py-2 rounded-md bg-destructive text-white text-sm font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50"
              >
                {processing === rejectModal.id ? "Rejeitando..." : "Confirmar rejeição"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
